import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, FolderKanban, User, Sparkles, Lock } from 'lucide-react';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import api from 'src/services/api';
import { InputIcon } from '../ui/input';
import { CommentsModal } from './CommentsModal';
import { FilterCombobox } from './FilterCombobox';

const ALL = '0'; // valor sentinela usado pela opção global dos filtros
const NO_STATUS = 'Sem status';

// Ordem fixa das colunas. Status fora da lista vão para o fim.
const STATUS_ORDER = [
  'new',
  'in progress',
  'ready for test',
  'ready',
  'done',
  'archived',
];

// Paleta usada quando o status não bate com nenhuma palavra-chave conhecida
const FALLBACK_COLORS = [
  { dot: '#64748B', bg: '#F1F5F9', text: '#475569', border: '#E2E8F0' },
  { dot: '#2563EB', bg: '#DBEAFE', text: '#1D4ED8', border: '#BFDBFE' },
  { dot: '#9333EA', bg: '#F3E8FF', text: '#7E22CE', border: '#E9D5FF' },
  { dot: '#0D9488', bg: '#CCFBF1', text: '#0F766E', border: '#99F6E4' },
];

const STATUS_COLORS = [
  {
    match: ['pronto (em homologacao)', 'pronto em homologacao'],
    dot: '#0D9488',
    bg: '#CCFBF1',
    text: '#0F766E',
    border: '#99F6E4',
  },
  {
    match: ['new', 'novo'],
    dot: '#F97316',
    bg: '#FFEDD5',
    text: '#C2410C',
    border: '#FED7AA',
  },
  {
    match: ['pendente', 'backlog', 'todo', 'a fazer'],
    dot: '#64748B',
    bg: '#F1F5F9',
    text: '#475569',
    border: '#E2E8F0',
  },
  {
    match: ['ready for test', 'ready for tests', 'pronto para teste'],
    dot: '#EAB308',
    bg: '#FEF3C7',
    text: '#B45309',
    border: '#FDE68A',
  },
  {
    match: ['ready'],
    dot: '#9333EA',
    bg: '#F3E8FF',
    text: '#7E22CE',
    border: '#E9D5FF',
  },
  {
    match: ['conclu', 'done', 'finaliz', 'closed', 'fechad'],
    dot: '#22C55E',
    bg: '#DCFCE7',
    text: '#15803D',
    border: '#BBF7D0',
  },
  {
    match: ['cancel', 'bloquead', 'blocked', 'archived'],
    dot: '#EF4444',
    bg: '#FEE2E2',
    text: '#B91C1C',
    border: '#FECACA',
  },
];

// Ignora maiúsculas, espaços extras e "in-progress" / "in_progress"
function statusKey(s) {
  return normalize(s).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function normalize(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function getStatusColor(status, index) {
  const normalizedStatus = normalize(status);
  const found = STATUS_COLORS.find((color) =>
    color.match.some((match) => normalizedStatus.includes(normalize(match))),
  );

  return found || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

export function KanbanCard({ card, onClick }) {
  const avancou = card.avancou_ontem;
  const blocked = card.bloqueada === true;
  const responsaveis = Array.isArray(card.responsaveis)
    ? card.responsaveis
    : [];
  const responsaveisDoCard = [
    ...responsaveis,
    card.usuario_colaborador
      ? {
          id_colaborador: card.id_responsavel,
          usuario_colaborador: card.usuario_colaborador,
        }
      : null,
  ].filter(
    (responsavel, index, todos) =>
      responsavel?.usuario_colaborador &&
      todos.findIndex((outro) => {
        if (!outro?.usuario_colaborador) return false;
        if (
          responsavel.id_colaborador !== undefined &&
          responsavel.id_colaborador !== null &&
          outro.id_colaborador !== undefined &&
          outro.id_colaborador !== null
        ) {
          return String(outro.id_colaborador) === String(responsavel.id_colaborador);
        }
        return outro.usuario_colaborador === responsavel.usuario_colaborador;
      }) === index,
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(card)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(card);
        }
      }}
      className={`cursor-pointer rounded-lg border p-3 shadow-sm transition-shadow hover:shadow-md ${
        blocked
          ? 'border-red-300 bg-red-50'
          : avancou
            ? 'border-emerald-300 bg-emerald-50'
            : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 break-words text-sm font-semibold leading-snug text-[#020817]">
          {card.titulo_hu}
        </p>

        <div className="flex shrink-0 items-center gap-1.5">
          {blocked && (
            <span
              title={card.observacao_bloqueio || 'Esta tarefa está bloqueada'}
              className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700"
            >
              <Lock className="h-3.5 w-3.5" />
            </span>
          )}

          {avancou && (
            <span
              title={"Saiu de 'in progress' para pronto ontem"}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700"
            >
              <Sparkles className="h-4 w-4" />
            </span>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-700">
        <FolderKanban className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate" title={card.nome_projeto}>
          {card.nome_projeto}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-1.5 border-t border-gray-100 pt-2 text-xs text-gray-500">
        <User className="h-3.5 w-3.5 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-wrap gap-1">
          {responsaveisDoCard.map((responsavel) => (
            <span
              key={
                responsavel.id_colaborador ?? responsavel.usuario_colaborador
              }
              className="inline-flex max-w-full items-center truncate rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[12px] font-medium text-slate-600 transition-colors"
              title={responsavel.usuario_colaborador}
            >
              {responsavel.usuario_colaborador}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function KanbanColumn({ status, cards, color, onCardClick }) {
  return (
    <div
      className="flex w-[280px] shrink-0 flex-col rounded-xl border"
      style={{ borderColor: color.border, backgroundColor: '#FAFAFA' }}
    >
      <div
        className="flex items-center justify-between rounded-t-xl px-3 py-2"
        style={{ backgroundColor: color.bg, color: color.text }}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: color.dot }}
          />
          {status}
        </div>
        <span className="text-sm font-semibold">{cards.length}</span>
      </div>

      <div className="flex min-h-[200px] max-h-[600px] flex-1 flex-col gap-2 overflow-y-auto p-2">
        {cards.length === 0 ? (
          <p className="mt-6 text-center text-xs text-gray-400">
            Nenhuma tarefa neste status
          </p>
        ) : (
          cards.map((card) => (
            <KanbanCard
              key={card.id_historia_de_usuario}
              card={card}
              onClick={onCardClick}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function Kanban() {
  const [cards, setCards] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [projetoSelecionado, setProjetoSelecionado] = useState(ALL);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(ALL);
  const [termoPesquisa, setTermoPesquisa] = useState('');
  const [cardSelecionado, setCardSelecionado] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Opções dos filtros (carrega uma única vez)
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [projRes, userRes] = await Promise.all([
          api.get('/api/planejamentoProjetos'),
          api.get('/api/buscarUsuarios'),
        ]);
        setProjetos(projRes.data || []);
        const sortedUsers = Array.isArray(userRes.data)
          ? [...userRes.data].sort((a, b) =>
              (a.nome_usuario || a.name || '').localeCompare(
                b.nome_usuario || b.name || '',
                'pt-BR',
                { sensitivity: 'base' },
              ),
            )
          : [];

        setUsuarios(sortedUsers);
      } catch (err) {
        console.error('Erro ao carregar filtros do kanban:', err);
      }
    };
    loadFilters();
  }, []);

  // Cards: refaz a busca quando muda projeto e/ou usuário
  useEffect(() => {
    let cancelled = false;

    const loadCards = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (projetoSelecionado !== ALL) params.project = projetoSelecionado;
        if (usuarioSelecionado !== ALL) params.user = usuarioSelecionado;

        const { data } = await api.get('/kanban', { params });
        if (!cancelled) setCards(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erro ao buscar dados do kanban:', err);
        if (!cancelled) {
          setCards([]);
          setError('Não foi possível carregar as tarefas. Tente novamente.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCards();
    // evita que uma resposta antiga sobrescreva uma mais recente
    return () => {
      cancelled = true;
    };
  }, [projetoSelecionado, usuarioSelecionado]);

  // Pesquisa por título (client-side)
  const cardsFiltrados = useMemo(() => {
    const term = normalize(termoPesquisa);
    if (!term) return cards;
    return cards.filter((c) => normalize(c.titulo_hu).includes(term));
  }, [cards, termoPesquisa]);

  // Agrupa por hu.status_hu mantendo a ordem em que cada status aparece
  const columns = useMemo(() => {
    const groups = new Map();

    // Cria todas as colunas conhecidas, mesmo sem tarefas.
    STATUS_ORDER.forEach((status) => {
      groups.set(status, []);
    });

    // Mantém os status adicionais que existam nos dados.
    cardsFiltrados.forEach((card) => {
      const status = card.status_hu || NO_STATUS;
      const normalizedStatus = statusKey(status);

      const existingStatus = Array.from(groups.keys()).find(
        (item) => statusKey(item) === normalizedStatus,
      );

      if (existingStatus) {
        groups.get(existingStatus).push(card);
      } else {
        groups.set(status, [card]);
      }
    });

    return Array.from(groups, ([status, items]) => ({
      status,
      items,
    }));
  }, [cardsFiltrados]);

  return (
    <div className="w-full">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-4 p-4 pb-6">
        <InputIcon
          placeholder="Buscar tarefas..."
          className="w-64 py-0"
          value={termoPesquisa}
          onChange={(e) => setTermoPesquisa(e.target.value)}
          icon={<MagnifyingGlassIcon className="h-[18px] w-[16px]" />}
        />

        <div className="w-full sm:w-[240px]">
          <FilterCombobox
            label="Filtrar por projeto"
            searchPlaceholder="Buscar projeto..."
            emptyMessage="Nenhum projeto encontrado."
            options={[
              { value: ALL, label: 'Todos os projetos' },
              ...projetos.map((projeto) => ({
                value: String(projeto.id),
                label: projeto.nome,
              })),
            ]}
            value={projetoSelecionado}
            onValueChange={setProjetoSelecionado}
          />
        </div>

        <div className="w-full sm:w-[240px]">
          <FilterCombobox
            label="Filtrar por responsável"
            searchPlaceholder="Buscar responsável..."
            emptyMessage="Nenhum responsável encontrado."
            options={[
              { value: ALL, label: 'Todos os responsáveis' },
              ...usuarios.map((usuario) => ({
                value: String(usuario.id),
                label: usuario.nome_usuario || usuario.name || 'Responsável',
              })),
            ]}

            value={usuarioSelecionado}
            onValueChange={setUsuarioSelecionado}
          />
        </div>
      </div>

      {/* Quadro */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando tarefas...
        </div>
      ) : error ? (
        <p className="py-20 text-center text-red-600">{error}</p>
      ) : cardsFiltrados.length === 0 ? (
        <p className="py-20 text-center text-gray-500">
          Nenhuma tarefa encontrada para os filtros selecionados.
        </p>
      ) : (
        <div className="flex w-full min-w-0 gap-4 overflow-x-auto px-4 pb-4">
          {columns.map(({ status, items }, index) => (
            <KanbanColumn
              key={status}
              status={status}
              cards={items}
              color={getStatusColor(status, index)}
              onCardClick={setCardSelecionado}
            />
          ))}
        </div>
      )}
      <CommentsModal
        card={cardSelecionado}
        onClose={() => setCardSelecionado(null)}
      />
    </div>
  );
}
